// ────────────────────────────────────────────────────────────────────────
// Calendario de eventos del forecasting (feriados, picos, campañas).
//
// CRUD directo contra microservice-forecasting (forecasting/events) vía BFF.
// El scope (global / tenant) lo decide el backend según el rol del token; este
// formulario no lo expone. Cada evento lleva un "impacto esperado" (factor) que
// ajusta multiplicativamente el forecast en sus fechas (0.4 = -60% feriado,
// 1.8 = +80% CyberMonday). El sistema puede sugerir el factor desde el histórico.
// ────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, CalendarPlus, Info, AlertTriangle, Wand2 } from "lucide-react";
import dayjs from "dayjs";
import { listEvents, createEvent, deleteEvent, getEffectSuggestion } from "api/forecasting";
import { cn } from "../../../utils/cn";
import { Modal } from "../../../components/ui/Modal";
import i18n from "../../../i18n/index";

const KIND_META = {
  holiday: { label: "Feriado", tone: "bg-status-info-soft text-status-info", effect: 0.5 },
  peak: { label: "Pico de demanda", tone: "bg-status-warning-soft text-status-warning", effect: 1.6 },
  promo: { label: "Campaña / Promo", tone: "bg-brand-50 text-brand-700", effect: 1.5 },
  other: { label: "Otro", tone: "bg-status-neutral-soft text-fg-muted", effect: 1.0 },
};
const SCOPE_LABEL = { global: "Global", logistic: "Mi operación", company: "Empresa" };
const APPLIES_OPTIONS = [
  { value: "created", label: "Pedidos creados" },
  { value: "delivered", label: "Entregas" },
  { value: "delivered_before_sla", label: "Entregas en SLA" },
  { value: "all", label: "Todas las métricas" },
];
const ALL_METRICS = ["created", "delivered", "delivered_before_sla"];

const inputCls =
  "w-full bg-bg border border-border rounded-lg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors";

function errMessage(err) {
  const body = err?.response?.data;
  return (typeof body === "string" ? body : body?.message) || err?.message || i18n.t("Ocurrió un error");
}

const fechaCorta = (iso) => new Date(`${iso}T00:00:00`);
// Feriado manual global con fecha fija anual: el motor lo ignora porque los
// feriados oficiales (incluidos los móviles) salen del calendario del país.
const isSupersededHoliday = (e) => e.kind === "holiday" && e.scopeType === "global" && e.recurrence === "annual";
const pct = (f) => {
  const p = Math.round((Number(f) - 1) * 100);
  return p === 0 ? "neutro" : p > 0 ? `+${p}%` : `${p}%`;
};

export default function EventsModal({ onClose, onChange }) {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // {suggestedEffect, samples} | null
  const [suggesting, setSuggesting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    kind: "peak",
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: "",
    note: "",
    effect: KIND_META.peak.effect,
    applies: "created",
    recurring: false,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listEvents({});
      setList(data);
      onChange?.(data);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar el tipo, proponemos su factor por defecto y limpiamos la sugerencia.
  const handleKind = (kind) =>
    setForm((f) => ({ ...f, kind, effect: KIND_META[kind]?.effect ?? 1 }));

  const appliesToList = (applies) => (applies === "all" ? [...ALL_METRICS] : [applies]);

  const handleSuggest = async () => {
    if (!form.startDate) return;
    setSuggesting(true);
    setSuggestion(null);
    try {
      const metric = form.applies === "all" ? "created" : form.applies;
      const s = await getEffectSuggestion({
        metric,
        startDate: form.startDate,
        endDate: form.endDate || undefined,
        recurrence: form.recurring ? "annual" : "none",
      });
      setSuggestion(s || { suggestedEffect: null, samples: 0 });
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSuggesting(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.startDate) return;
    setSaving(true);
    setError(null);
    try {
      await createEvent({
        name: form.name.trim(),
        kind: form.kind,
        startDate: form.startDate,
        endDate: form.endDate || undefined,
        note: form.note.trim() || undefined,
        effect: Number(form.effect),
        appliesTo: appliesToList(form.applies),
        recurrence: form.recurring ? "annual" : "none",
      });
      setForm((f) => ({ ...f, name: "", endDate: "", note: "" }));
      setSuggestion(null);
      await refresh();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError(null);
    try {
      await deleteEvent(id);
      await refresh();
    } catch (err) {
      setError(errMessage(err));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
            <CalendarPlus className="w-[18px] h-[18px]" />
          </span>
          <span className="min-w-0">
            <span className="block text-base font-semibold text-fg leading-tight">{t('Calendario de eventos')}</span>
            <span className="block text-xs text-fg-muted font-normal">
              {t('Feriados, picos y campañas que mueven la demanda')}
            </span>
          </span>
        </span>
      }
      bodyClassName="p-0"
      footer={
        <div className="flex items-start gap-2 flex-1">
          <Info className="w-3.5 h-3.5 text-fg-subtle shrink-0 mt-0.5" />
          <p className="text-[11px] text-fg-muted leading-relaxed">
            {t('El impacto multiplica la demanda esos días (1 = neutro, 1.30 = +30%, 0.40 = feriado). El forecast lo aplica en la próxima corrida.')}
          </p>
        </div>
      }
    >
      <div className="p-5 space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-status-error-soft border border-status-error/30 p-3 text-sm text-fg">
            <AlertTriangle className="w-4 h-4 text-status-error shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Form alta */}
        <form onSubmit={handleAdd} className="rounded-lg border border-border bg-bg-muted/40 p-4 space-y-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-fg-muted">{t('Nombre del evento')}</span>
            <input
              type="text"
              className={inputCls}
              placeholder={t('Ej: CyberDay, Black Friday, elecciones…')}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-fg-muted">{t('Tipo')}</span>
              <select className={inputCls} value={form.kind} onChange={(e) => handleKind(e.target.value)}>
                {Object.entries(KIND_META).map(([k, v]) => (
                  <option key={k} value={k}>{t(v.label)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-fg-muted">{t('Desde')}</span>
              <input
                type="date"
                className={inputCls}
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                required
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-fg-muted">
                {t('Hasta')} <span className="text-fg-subtle font-normal">({t('opcional')})</span>
              </span>
              <input
                type="date"
                className={inputCls}
                min={form.startDate}
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-fg-muted">
                {t('Impacto esperado')} <span className="text-fg-subtle font-normal">({pct(form.effect)})</span>
              </span>
              <input
                type="number"
                step="0.05"
                min="0"
                max="5"
                className={inputCls}
                value={form.effect}
                onChange={(e) => setForm((f) => ({ ...f, effect: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-fg-muted">{t('Aplica a')}</span>
              <select
                className={inputCls}
                value={form.applies}
                onChange={(e) => setForm((f) => ({ ...f, applies: e.target.value }))}
              >
                {APPLIES_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{t(o.label)}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Sugerencia del histórico + recurrencia */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleSuggest}
                disabled={suggesting || !form.startDate}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline disabled:opacity-60"
              >
                <Wand2 className="w-3.5 h-3.5" />
                {suggesting ? t('Calculando…') : t('Sugerir del histórico')}
              </button>
              {suggestion && (
                suggestion.suggestedEffect != null ? (
                  <span className="text-[11px] text-fg-muted">
                    {t('histórico sugiere')} <strong className="text-fg">×{suggestion.suggestedEffect}</strong> · n={suggestion.samples}{" "}
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, effect: suggestion.suggestedEffect }))}
                      className="text-brand-700 hover:underline font-medium"
                    >
                      {t('usar')}
                    </button>
                  </span>
                ) : (
                  <span className="text-[11px] text-fg-subtle">{t('sin histórico suficiente para sugerir')}</span>
                )
              )}
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs text-fg-muted cursor-pointer">
              <input
                type="checkbox"
                checked={form.recurring}
                onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.checked }))}
                className="rounded border-border"
              />
              {t('Se repite cada año')}
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-fg-muted">{t('Nota (opcional)')}</span>
            <input
              type="text"
              className={inputCls}
              placeholder={t('Contexto del evento')}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </label>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="inline-flex items-start gap-1.5 text-[11px] text-fg-muted max-w-sm">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {t('Los feriados oficiales de Chile se aplican automáticamente, con su efecto medido en tu histórico. Carga aquí solo feriados puntuales (p. ej. elecciones) o eventos propios.')}
            </p>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold transition-colors disabled:opacity-60"
            >
              <Plus className="w-4 h-4" />
              {t('Agregar evento')}
            </button>
          </div>
        </form>

        {/* Lista */}
        {loading ? (
          <p className="text-sm text-fg-muted text-center py-6">{t('Cargando…')}</p>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center gap-2 text-center py-8 rounded-lg border border-dashed border-border bg-bg-muted/30">
            <CalendarPlus className="w-8 h-8 text-fg-subtle" />
            <p className="text-sm font-medium text-fg">{t('Aún no hay eventos cargados')}</p>
            <p className="text-xs text-fg-muted max-w-xs">
              {t('Agrega picos, campañas o feriados puntuales para que el forecast los considere. Los feriados oficiales ya están incluidos.')}
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {list.map((e) => {
              const meta = KIND_META[e.kind] || KIND_META.other;
              const start = fechaCorta(e.startDate);
              const multiDay = e.endDate && e.endDate !== e.startDate;
              const effect = e.effect != null ? Number(e.effect) : meta.effect;
              const superseded = isSupersededHoliday(e);
              return (
                <li key={e.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-bg">
                  <div className="text-center shrink-0 w-12">
                    <div className="text-lg font-bold text-fg leading-none tabular-nums">{start.getDate()}</div>
                    <div className="text-[10px] uppercase text-fg-muted">
                      {start.toLocaleDateString("es-CL", { month: "short" })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-fg truncate">{e.name}</span>
                      <span className={cn("px-1.5 py-0.5 rounded-full text-[10px] font-semibold", meta.tone)}>
                        {t(meta.label)}
                      </span>
                      {e.scopeType === "global" && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-status-neutral-soft text-fg-muted">
                          {t(SCOPE_LABEL.global)}
                        </span>
                      )}
                      {e.recurrence === "annual" && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-status-neutral-soft text-fg-muted">
                          {t('anual')}
                        </span>
                      )}
                      {superseded && (
                        <span
                          className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-status-info-soft text-status-info"
                          title={t('El forecast usa el calendario oficial de Chile con la fecha real de cada año; este evento ya no se aplica.')}
                        >
                          {t('Reemplazado por calendario oficial')}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-fg-muted mt-0.5">
                      {start.toLocaleDateString("es-CL", { weekday: "short", day: "numeric", month: "short" })}
                      {multiDay && ` → ${fechaCorta(e.endDate).toLocaleDateString("es-CL", { day: "numeric", month: "short" })}`}
                      {e.note ? ` · ${e.note}` : ""}
                    </div>
                  </div>
                  <span
                    className={cn(
                      "text-xs font-bold tabular-nums w-14 text-right shrink-0",
                      superseded
                        ? "text-fg-subtle line-through"
                        : effect >= 1.2 ? "text-status-warning" : effect < 1 ? "text-status-info" : "text-fg-muted",
                    )}
                    title={superseded ? t('No se aplica: lo reemplaza el calendario oficial') : t('Impacto en la demanda')}
                  >
                    {pct(effect)}
                  </span>
                  <button
                    onClick={() => handleDelete(e.id)}
                    className="w-8 h-8 rounded-md text-fg-muted hover:bg-status-error-soft hover:text-status-error flex items-center justify-center transition-colors shrink-0"
                    aria-label={t('Eliminar')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
